import { useState } from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import { TextField, Button, Chip, Box, Stack, Typography } from '@mui/material';

import { sendScanReportToEmail } from '../../../../services/scanSession.service';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';

import { ReaderReport } from '../../../../types/ReaderReport';
import { ProcessStatus } from '../../../../types/ProcessStatus';
import { OverviewCard } from '../../../common/OverviewCard/OverviewCard';
import { tablePrimaryButtonSx } from '../../../../styles/tableButtonStyles';

export default function ShareReport({ report }: { report: ReaderReport }) {
  const [currentEmail, setCurrentEmail] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<null | 'invalid' | 'duplicate'>(null);
  const [loading, setLoading] = useState(false);

  const notifyMessage = useNotifyMessage();

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCurrentEmail(value);

    if (!value || isValidEmail(value)) {
      setError(null);
    } else {
      setError('invalid');
    }
  };

  const addEmail = () => {
    if (!currentEmail) return;

    if (!isValidEmail(currentEmail)) {
      setError('invalid');
      return;
    }

    if (emails.includes(currentEmail)) {
      setError('duplicate');
      return;
    }

    setEmails([...emails, currentEmail]);
    setCurrentEmail('');
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail();
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const handleShare = async () => {
    const finalEmails = [...emails];

    if (currentEmail && isValidEmail(currentEmail)) {
      if (!emails.includes(currentEmail)) {
        finalEmails.push(currentEmail);
      } else {
        setError('duplicate');
        return;
      }
    }

    if (finalEmails.length === 0) {
      setError('invalid');
      return;
    }

    if (!report?.reportId) {
      return;
    }

    const status = report?.scanSession?.processStatus;

    if (status === ProcessStatus.Error || status === ProcessStatus.Pending) {
      notifyMessage?.notifyMessage?.('Cannot share report while scan session is in progress or has failed.', 'error');
      setEmails([]);
      setCurrentEmail('');
      return;
    }

    setLoading(true);
    try {
      await sendScanReportToEmail(report.reportId, { emailAddresses: finalEmails });
      notifyMessage?.notifyMessage?.(`Scan report shared with ${finalEmails.join(', ')}`);

      setEmails([]);
      setCurrentEmail('');
      setError(null);
    } catch (error) {
      console.error('Error sharing report:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <OverviewCard title='Share Report'>
        <Stack spacing={1.5} sx={{ px: { xs: 0.75, sm: 1.25 }, pb: 0.5 }}>
          {emails.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {emails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={() => removeEmail(email)}
                  deleteIcon={<CloseIcon />}
                  color='primary'
                  size='small'
                  sx={{ borderRadius: '10px' }}
                />
              ))}
            </Box>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            spacing={1}
            sx={{ my: 1, px: { xs: 0.75, sm: 1 }, py: 0.75 }}
          >
            <TextField
              id='userEmailId'
              placeholder='Enter email addresses (press Enter or comma to add)'
              variant='outlined'
              size='small'
              fullWidth
              value={currentEmail}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              error={!!error}
              sx={{ my: 0.5, '& .MuiInputBase-root': { pr: 0.5 } }}
              helperText={
                error === 'invalid' ? 'Please enter a valid email address' : error === 'duplicate' ? "You've already entered this email address" : ''
              }
            />
            <Stack direction='row' spacing={1} sx={{ flexShrink: 0, my: 0.5, px: { xs: 0, md: 0.25 }, py: 0.25 }}>
              <Button
                variant='outlined'
                onClick={addEmail}
                disabled={!currentEmail || !!error}
                sx={{ textTransform: 'none', borderRadius: '12px', px: 2, py: 0.75 }}
              >
                Add
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={handleShare}
                disabled={(emails.length === 0 && !currentEmail) || loading}
                sx={tablePrimaryButtonSx}
              >
                {loading ? 'Sharing...' : 'Share'}
              </Button>
            </Stack>
          </Stack>

          {emails.length > 0 && (
            <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {emails.length} {emails.length === 1 ? 'recipient' : 'recipients'} added
            </Typography>
          )}
        </Stack>
      </OverviewCard>
    </Box>
  );
}
